import React, { useState, useEffect, useRef } from "react";
import { Input, Select, Button, Tag, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ShoppingCartOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  POSCartItem,
  POSSale,
  POSPayment,
  PharmacyBatch,
} from "../../interfaces/POS";
import { PharmacyItem } from "../../interfaces/PharmacyItems";
import { Patient } from "../../interfaces/Patient";
import { getPharmacyItems, getPharmacyItemById } from "../../apis/pharmacyItems";
import { getPatients } from "../../apis/patient";
import { getUsers } from "../../apis/user";
import { createPOSSale } from "../../apis/pos";
import { getProductBundles, getProductBundleById } from "../../apis/productBundles";
import { ProductBundle } from "../../interfaces/ProductBundle";
import { useNotification } from "../../NotificationContext";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import PaymentModal, { PaymentData } from "../../components/PaymentModal";
import "react-datepicker/dist/react-datepicker.css";
import { User } from "../../interfaces/User";
import { log } from "console";
const { Option } = Select;

const PharmacyPOS: React.FC = () => {
  const { showNotification } = useNotification();
  const [searchQuery, setSearchQuery] = useState("");
  const [pharmacyItems, setPharmacyItems] = useState<PharmacyItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<PharmacyItem[]>([]);
  const [cart, setCart] = useState<POSCartItem[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [saleDate, setSaleDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState<string>("");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [discount, setDiscount] = useState<number>(0); // Discount amount (will be converted to percentage)
  const [discountPercentage, setDiscountPercentage] = useState<number>(0); // Order level discount percentage
  const searchInputRef = useRef<any>(null);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [selectedItemForBatch, setSelectedItemForBatch] =
    useState<PharmacyItem | null>(null);
  const [selectedCartItemForBatch, setSelectedCartItemForBatch] = useState<
    string | null
  >(null);
  const [availableBatches, setAvailableBatches] = useState<PharmacyBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<PharmacyBatch | null>(
    null
  );
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [quantityToAdd, setQuantityToAdd] = useState(1);
  const [bundles, setBundles] = useState<ProductBundle[]>([]);
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);
  const [bundleDetail, setBundleDetail] = useState<ProductBundle | null>(null);
  const [bundleDetailLoading, setBundleDetailLoading] = useState(false);

  // Fetch pharmacy items
  useEffect(() => {
    fetchPharmacyItems();
    fetchPatients();
    fetchUsers();
    fetchBundles();
  }, []);

  const fetchBundles = async () => {
    try {
      const res: any = await getProductBundles();
      const data = Array.isArray(res) ? res : res?.data ?? [];
      setBundles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch bundles:", err);
    }
  };

  useEffect(() => {
    if (!selectedBundleId) {
      setBundleDetail(null);
      return;
    }
    setBundleDetailLoading(true);
    getProductBundleById(selectedBundleId)
      .then(async (res: any) => {
        const b = res?.data ?? res ?? null;
        if (!b?.items?.length) {
          setSelectedBundleId(null);
          setBundleDetail(null);
          return;
        }
        // Resolve pharmacy items with batches: for batch-tracked bundle items, fetch full item if list item has no batches
        const itemsWithBatches: PharmacyItem[] = [...pharmacyItems];
        for (const bi of b.items) {
          const itemId = bi.pharmacy_item_id ?? bi.pharmacyItemId ?? bi.pharmacy_item?.id ?? bi.pharmacyItem?.id;
          if (!itemId) continue;
          const fromList = pharmacyItems.find((p) => p.id === itemId);
          const isBatchTracked = fromList?.is_batch_tracked ?? (fromList as any)?.isBatchTracked ?? (bi.pharmacy_item as any)?.is_batch_tracked ?? (bi.pharmacyItem as any)?.isBatchTracked ?? false;
          const hasBatches = fromList?.batches && (Array.isArray(fromList.batches) ? fromList.batches.length > 0 : true);
          if (isBatchTracked && (!fromList || !hasBatches)) {
            try {
              const fetched: any = await getPharmacyItemById(itemId);
              const fullItem = fetched?.data ?? fetched;
              const batches = fullItem?.batches ?? [];
              if (fullItem && (fullItem.id ?? fullItem.pharmacy_item_id) && batches.length > 0) {
                const resolved = { ...(fromList || {}), ...fullItem, id: fullItem.id ?? fullItem.pharmacy_item_id ?? itemId, batches };
                const idx = itemsWithBatches.findIndex((p) => (p.id ?? (p as any).pharmacy_item_id) === itemId);
                if (idx >= 0) itemsWithBatches[idx] = resolved;
                else itemsWithBatches.push(resolved);
              }
            } catch (_) {
              // keep list item if fetch fails
            }
          }
        }
        const newCartItems = buildCartItemsFromBundle(b, itemsWithBatches);
        if (newCartItems.length > 0) {
          setCart((prev) => [...prev, ...newCartItems]);
        }
        setSelectedBundleId(null);
        setBundleDetail(null);
      })
      .catch((err: any) => {
        console.error("Failed to fetch bundle detail:", err);
        setBundleDetail(null);
        showNotification("error", "Failed to load bundle", "", "topRight");
      })
      .finally(() => setBundleDetailLoading(false));
  }, [selectedBundleId, pharmacyItems]);

  // Filter items based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = pharmacyItems.filter(
      (item) =>
        (item.name ?? "").toLowerCase().includes(query) ||
    (item.sku ?? "").toLowerCase().includes(query) ||
    (item.barcode ?? "").toLowerCase().includes(query) ||
    (item.molecule ?? "").toLowerCase().includes(query)
    );
    setFilteredItems(filtered.slice(0, 10)); // Limit to 10 results
  }, [searchQuery, pharmacyItems]);

  const fetchPharmacyItems = async () => {
    try {
      const response: any = await getPharmacyItems({
        per_page: 1000,
        is_active: true,
      });
      // Handle the response structure: { data: [...], links: {...}, meta: {...} }
      const items = Array.isArray(response) ? response : response?.data || [];
      setPharmacyItems(items || []);
    } catch (error) {
      console.error("Error fetching pharmacy items:", error);
      showNotification(
        "error",
        "Failed to load pharmacy items",
        "",
        "topRight"
      );
    }
  };

  const fetchPatients = async () => {
    try {
      const response: any = await getPatients({ per_page: 100 });
      setPatients(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response: any = await getUsers({ per_page: 100 });
      setUsers(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
  const userStr = localStorage.getItem("user");

  if (!userStr || users.length === 0) return;

  try {
    const loggedInUser: User = JSON.parse(userStr);

    const matchedUser = users.find(
      (u: User) => u.id === loggedInUser.id
    );

    if (matchedUser && matchedUser.id) {
      setSelectedUserId(matchedUser.id);
    }
  } catch (err) {
    console.error("Invalid user object in localStorage");
  }
}, [users]);

  // Get default unit price from item (first usable batch or 0) for non-batch-tracked add
  // Get tax percentage from batch (API: batch.tax_percentage or batch.grn_item.tax_percentage)
  const getTaxPercentageFromBatch = (batch: any): number => {
    if (!batch) return 0;
    const raw = batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage;
    const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  // Reverse-calculate taxable from inclusive MRP: taxable = inclusive_price / (1 + gst%)
  const getTaxableFromMrp = (mrp: number, taxPercentage: number): number => {
    if (!taxPercentage || !Number.isFinite(taxPercentage)) return mrp;
    const divisor = 1 + taxPercentage / 100;
    const taxable = mrp / divisor;
    return Number.isFinite(taxable) ? taxable : mrp;
  };

  const getDefaultUnitPrice = (item: PharmacyItem): number => {
    const raw = item?.batches || [];
    const usable = raw
      .map((batch: any) => ({
        selling_price: batch.sellingPrice ?? batch.selling_price ?? "0",
        available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
        is_active: batch.isActive ?? batch.is_active ?? true,
        is_expired: batch.isExpired ?? batch.is_expired ?? false,
      }))
      .filter(
        (b: any) =>
          b.available_quantity > 0 && b.is_active && !b.is_expired
      );
    if (usable.length === 0) return 0;
    return parseFloat(usable[0].selling_price || "0") || 0;
  };

  // Add item to cart without batch (when is_batch_tracked is false)
  const addToCartWithoutBatch = (item: PharmacyItem, quantity: number = 1) => {
    const firstBatch = (item.batches || [])[0];
    const sellingPrice = getDefaultUnitPrice(item);
    const mrp = (firstBatch as any)?.mrp ?? sellingPrice;
    const taxPct = getTaxPercentageFromBatch(firstBatch);
    const unitPrice = getTaxableFromMrp(mrp, taxPct);
    const qty = quantity >= 1 ? quantity : 1;
    const newCartItem: POSCartItem = {
      id: `item-${Date.now()}-${item.id}`,
      pharmacyItemId: item.id!,
      name: item.name,
      sku: item.sku,
      barcode: item.barcode,
      quantity: qty,
      unitPrice,
      mrp,
      discount: 0,
      discountPercentage: 0,
      tax: 0,
      taxPercentage: taxPct,
      total: (unitPrice * qty) * (1 - 0 / 100) * (1 + (taxPct || 0) / 100),
      form: item.form,
      strength: item.strength,
      unit: item.unit,
    };
    setCart([...cart, newCartItem]);
    setSearchQuery("");
    setFilteredItems([]);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // Check if item has at least one usable batch (same logic as fetchBatchesForItem)
  const hasUsableBatches = (item: PharmacyItem): boolean => {
    const raw = item?.batches || [];
    if (raw.length === 0) return false;
    const usable = raw
      .map((batch: any) => ({
        available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
        is_active: batch.isActive ?? batch.is_active ?? true,
        is_expired: batch.isExpired ?? batch.is_expired ?? false,
      }))
      .filter(
        (b: any) =>
          b.available_quantity > 0 && b.is_active && !b.is_expired
      );
    return usable.length > 0;
  };

  // Get batches from pharmacy item instead of making API call
  const fetchBatchesForItem = (item: PharmacyItem) => {
  if (!item?.id) return;

  setIsLoadingBatches(true);

  try {
      // Get batches from the pharmacy item's batches array
      const batches = (item.batches || []).map((batch: any) => {
        // Map the batch structure to match PharmacyBatch interface
        // API returns camelCase, convert to snake_case for PharmacyBatch interface
        return {
          id: batch.id,
          batch_number: batch.batchNumber ?? batch.batch_number,
          manufacturing_date: batch.manufacturingDate ?? batch.manufacturing_date,
          expiry_date: batch.expiryDate ?? batch.expiry_date,
          purchase_price: (batch.purchasePrice ?? batch.purchase_price)?.toString() || "0",
          selling_price: (batch.sellingPrice ?? batch.selling_price)?.toString() || "0",
          available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
          is_expired: batch.isExpired ?? batch.is_expired ?? false,
          is_expiring_soon: batch.isExpiringSoon ?? batch.is_expiring_soon ?? false,
          supplier_id: batch.supplierId ?? batch.supplier_id,
          notes: batch.notes,
          is_active: batch.isActive ?? batch.is_active ?? true,
          mrp: parseFloat(batch.sellingPrice || batch.selling_price || batch.purchasePrice || batch.purchase_price || "0"),
          tax_percentage: batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage,
        };
      }).filter((batch: any) => batch.available_quantity > 0 && batch.is_active && !batch.is_expired);

    setAvailableBatches(batches);

      // Auto-select if only one usable batch
    if (batches.length === 1) {
      setSelectedBatch(batches[0]);
    } else {
      setSelectedBatch(null);
    }
  } catch (error) {
      console.error("Error processing batches:", error);
    setAvailableBatches([]);
    setSelectedBatch(null);
  } finally {
    setIsLoadingBatches(false);
  }
};

   const getItemDiscountPercentageFromBatch = (batch: any): number => {
    console.log({batch});
    
    if (!batch) return 0;
    const raw = batch.discount_percentage ?? batch.discountPercentage ?? batch.grnItem?.discountPercentage ?? batch.grnItem?.discount_percentage;
    const n = typeof raw === "string" ? parseFloat(raw) : Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const calculateItemTotal = (item: POSCartItem): number => {
    const itemSubtotal = item.quantity * item.unitPrice;
    const discountPct = item.discountPercentage ?? 0;
    const itemDiscountAmount = itemSubtotal * (discountPct / 100);
    const itemAfterDiscount = itemSubtotal - itemDiscountAmount;
    const itemTaxAmount = itemAfterDiscount * ((item.taxPercentage || 0) / 100);
    return itemAfterDiscount + itemTaxAmount;
  };

  const handleItemSelect = (item: PharmacyItem) => {
    // Check if item already exists in cart
    const existingItem = cart.find(
      (cartItem) => cartItem.pharmacyItemId === item.id
    );

    if (existingItem) {
      // Update quantity for existing item
      setCart(
        cart.map((cartItem) => {
          if (cartItem.id === existingItem.id) {
            const updatedItem = {
              ...cartItem,
              quantity: cartItem.quantity + 1,
            };
            return {
              ...updatedItem,
              total: calculateItemTotal(updatedItem),
            };
          }
          return cartItem;
        })
      );
      setSearchQuery("");
      setFilteredItems([]);
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    } else {
      // For items (non-bundle): always open batch modal so user can select batch before adding to cart
      setQuantityToAdd(1);
      setSelectedItemForBatch(item);
      fetchBatchesForItem(item);
      setIsBatchModalOpen(true);
    }
  };

  const addToCartWithBatch = () => {
    if (!selectedItemForBatch) return;

    const mrp = selectedBatch
      ? (selectedBatch.mrp ?? parseFloat(selectedBatch.selling_price || "0"))
      : getDefaultUnitPrice(selectedItemForBatch);
    const taxPct = getTaxPercentageFromBatch(selectedBatch ?? null);
    const unitPrice = getTaxableFromMrp(mrp, taxPct);

    // If updating an existing cart item
    if (selectedCartItemForBatch) {
      setCart(
        cart.map((cartItem) => {
          if (cartItem.id === selectedCartItemForBatch) {
            const updatedItem: POSCartItem = {
              ...cartItem,
              batchId: selectedBatch?.id,
              batchNumber: selectedBatch?.batch_number,
              expiryDate: selectedBatch?.expiry_date,
              unitPrice,
              mrp,
              taxPercentage: taxPct,
              discountPercentage: getItemDiscountPercentageFromBatch(selectedBatch ?? undefined),
            };
            return {
              ...updatedItem,
              total: calculateItemTotal(updatedItem),
            };
          }
          return cartItem;
        })
      );
    } else {
      // Adding new item to cart (use quantityToAdd when from bundle)
      const qty = quantityToAdd >= 1 ? quantityToAdd : 1;
      const newCartItem: POSCartItem = {
        id: `item-${Date.now()}-${selectedItemForBatch.id}`,
        pharmacyItemId: selectedItemForBatch.id!,
        name: selectedItemForBatch.name,
        sku: selectedItemForBatch.sku,
        barcode: selectedItemForBatch.barcode,
        quantity: qty,
        unitPrice,
        mrp,
        discount: 0,
        discountPercentage: getItemDiscountPercentageFromBatch(selectedBatch ?? undefined),
        tax: 0,
        taxPercentage: taxPct,
        total: 0,
        form: selectedItemForBatch.form,
        strength: selectedItemForBatch.strength,
        unit: selectedItemForBatch.unit,
        batchId: selectedBatch?.id,
        batchNumber: selectedBatch?.batch_number,
        expiryDate: selectedBatch?.expiry_date,
      };
      newCartItem.total = calculateItemTotal(newCartItem);

      setCart([...cart, newCartItem]);
    }

    // Reset batch selection
    setIsBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setSelectedCartItemForBatch(null);
    setSelectedBatch(null);
    setAvailableBatches([]);
    setQuantityToAdd(1);
    setSearchQuery("");
    setFilteredItems([]);

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Build cart items from a bundle; use pharmacyItems from state for full item data (including batches)
  const buildCartItemsFromBundle = (bundle: ProductBundle, pharmacyItemsList: PharmacyItem[]): POSCartItem[] => {
    const newItems: POSCartItem[] = [];
    const baseId = Date.now();
    (bundle.items ?? []).forEach((bi: any, idx: number) => {
      const itemId = bi.pharmacy_item_id ?? bi.pharmacyItemId ?? bi.pharmacy_item?.id ?? bi.pharmacyItem?.id;
      const pharmacyItem = pharmacyItemsList.find((p) => p.id === itemId) ?? bi.pharmacy_item ?? bi.pharmacyItem;
      const qty = Math.max(1, bi.quantity ?? bi.sort_order ?? 1);
      if (!pharmacyItem?.id) return;

      const isBatchTracked = pharmacyItem.is_batch_tracked ?? pharmacyItem.isBatchTracked ?? false;
      if (isBatchTracked) {
        const batch = getBatchWithNearestExpiry(pharmacyItem);
        if (!batch) return;
        const mrp = batch.mrp ?? parseFloat(batch.selling_price || "0");
        const taxPct = getTaxPercentageFromBatch(batch);
        const unitPrice = getTaxableFromMrp(mrp, taxPct);
        const discountPct = getItemDiscountPercentageFromBatch(batch);
        const cartItem: POSCartItem = {
          id: `item-${baseId}-${pharmacyItem.id}-${batch.id}-${idx}`,
          pharmacyItemId: pharmacyItem.id,
          name: pharmacyItem.name,
          sku: pharmacyItem.sku ?? "",
          barcode: pharmacyItem.barcode,
          quantity: qty,
          unitPrice,
          mrp,
          discount: 0,
          discountPercentage: discountPct,
          tax: 0,
          taxPercentage: taxPct,
          total: 0,
          form: pharmacyItem.form,
          strength: pharmacyItem.strength,
          unit: pharmacyItem.unit,
          batchId: batch.id,
          batchNumber: batch.batch_number,
          expiryDate: batch.expiry_date,
        };
        cartItem.total = calculateItemTotal(cartItem);
        newItems.push(cartItem);
      } else {
        const sellingPrice = getDefaultUnitPrice(pharmacyItem);
        const firstBatch = (pharmacyItem.batches || [])[0];
        const mrp = (firstBatch as any)?.mrp ?? sellingPrice;
        const taxPct = getTaxPercentageFromBatch(firstBatch);
        const unitPrice = getTaxableFromMrp(mrp, taxPct);
        const lineTotal = (unitPrice * qty) * (1 + (taxPct || 0) / 100);
        newItems.push({
          id: `item-${baseId}-${pharmacyItem.id}-${idx}`,
          pharmacyItemId: pharmacyItem.id,
          name: pharmacyItem.name,
          sku: pharmacyItem.sku ?? "",
          barcode: pharmacyItem.barcode,
          quantity: qty,
          unitPrice,
          mrp,
          discount: 0,
          discountPercentage: 0,
          tax: 0,
          taxPercentage: taxPct,
          total: lineTotal,
          form: pharmacyItem.form,
          strength: pharmacyItem.strength,
          unit: pharmacyItem.unit,
        });
      }
    });
    return newItems;
  };

  // Get batch with nearest expiry (FEFO) from item's batches for bundle flow
  const getBatchWithNearestExpiry = (item: PharmacyItem): PharmacyBatch | null => {
    console.log({item});

    const raw = item?.batches || [];
    console.log({raw});
    
    const usable = raw
      .map((batch: any) => ({
        id: batch.id ?? batch.pharmacy_batch_id,
        batch_number: batch.batchNumber ?? batch.batch_number,
        manufacturing_date: batch.manufacturingDate ?? batch.manufacturing_date,
        expiry_date: batch.expiryDate ?? batch.expiry_date,
        purchase_price: batch.purchasePrice?.toString() ?? batch.purchase_price ?? "0",
        selling_price: batch.sellingPrice?.toString() ?? batch.selling_price ?? "0",
        available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
        is_expired: batch.isExpired ?? batch.is_expired ?? false,
        is_expiring_soon: batch.isExpiringSoon ?? batch.is_expiring_soon,
        supplier_id: batch.supplierId ?? batch.supplier_id,
        notes: batch.notes,
        is_active: batch.isActive ?? batch.is_active ?? true,
        mrp: parseFloat(batch.sellingPrice ?? batch.selling_price ?? batch.purchasePrice ?? batch.purchase_price ?? "0"),
        tax_percentage: batch.tax_percentage ?? batch.taxPercentage ?? batch.grn_item?.tax_percentage ?? batch.grn_item?.taxPercentage,
        grn_item: batch.grn_item,
      }))
      .filter((b: any) => (b.available_quantity ?? 1) > 0 && b.is_active && !b.is_expired);
    if (usable.length === 0) return null;
    usable.sort((a: any, b: any) => {
      const dateA = a.expiry_date ? new Date(a.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
      const dateB = b.expiry_date ? new Date(b.expiry_date).getTime() : Number.MAX_SAFE_INTEGER;
      return dateA - dateB;
    });
    return usable[0] as PharmacyBatch;
  };

  // Add bundle item to cart: use nearest-expiry batch when batch-tracked, else add without batch
  const addBundleItemToCart = (pharmacyItem: PharmacyItem, quantity: number) => {
    const qty = quantity >= 1 ? quantity : 1;
    if (pharmacyItem.is_batch_tracked) {
      const batch = getBatchWithNearestExpiry(pharmacyItem);
      if (!batch) {
        message.warning(`No usable batch for ${pharmacyItem.name}. Skipping.`);
        return;
      }
      const mrp = batch.mrp ?? parseFloat(batch.selling_price || "0");
      const taxPct = getTaxPercentageFromBatch(batch);
      const unitPrice = getTaxableFromMrp(mrp, taxPct);
      const newCartItem: POSCartItem = {
        id: `item-${Date.now()}-${pharmacyItem.id}-${batch.id}`,
        pharmacyItemId: pharmacyItem.id!,
        name: pharmacyItem.name,
        sku: pharmacyItem.sku,
        barcode: pharmacyItem.barcode,
        quantity: qty,
        unitPrice,
        mrp,
        discount: 0,
        discountPercentage: getItemDiscountPercentageFromBatch(batch),
        tax: 0,
        taxPercentage: taxPct,
        total: 0,
        form: pharmacyItem.form,
        strength: pharmacyItem.strength,
        unit: pharmacyItem.unit,
        batchId: batch.id,
        batchNumber: batch.batch_number,
        expiryDate: batch.expiry_date,
      };
      newCartItem.total = calculateItemTotal(newCartItem);
      setCart([...cart, newCartItem]);
    } else {
      addToCartWithoutBatch(pharmacyItem, qty);
    }
    setSearchQuery("");
    setFilteredItems([]);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const openBatchModalForBundleItem = (pharmacyItem: PharmacyItem, quantity: number) => {
    setSelectedItemForBatch(pharmacyItem);
    setSelectedCartItemForBatch(null);
    setQuantityToAdd(quantity >= 1 ? quantity : 1);
    fetchBatchesForItem(pharmacyItem);
    setIsBatchModalOpen(true);
  };

  const addToCart = (item: PharmacyItem) => {
    handleItemSelect(item);
  };

  const updateCartItemQuantity = (itemId: string, change: number) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + change);
          const updatedItem = {
            ...item,
            quantity: newQuantity,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  const updateCartItemPrice = (itemId: string, price: number) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            unitPrice: price,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  const updateCartItemDiscountPercentage = (itemId: string, discountPct: number) => {
    const clamped = Math.min(100, Math.max(0, discountPct));
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            discountPercentage: clamped,
          };
          return {
            ...updatedItem,
            total: calculateItemTotal(updatedItem),
          };
        }
        return item;
      })
    );
  };

  // When tax % changes: recalc taxable from MRP (taxable = mrp / (1 + gst%)) and update unitPrice
  const updateCartItemTaxPercentage = (itemId: string, taxPct: number) => {
    const clamped = Math.min(100, Math.max(0, taxPct));
    setCart(
      cart.map((item) => {
        if (item.id !== itemId) return item;
        const mrp = item.mrp ?? item.unitPrice * (1 + (item.taxPercentage || 0) / 100);
        const newUnitPrice = getTaxableFromMrp(mrp, clamped);
        const updatedItem: POSCartItem = {
          ...item,
          taxPercentage: clamped,
          unitPrice: newUnitPrice,
        };
        return { ...updatedItem, total: calculateItemTotal(updatedItem) };
      })
    );
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      return sum + itemSubtotal;
    }, 0);
  };

  const calculateTotalDiscount = () => {
    // Calculate item-level discounts
    const itemDiscounts = cart.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemDiscount =
        itemSubtotal * ((item.discountPercentage || 0) / 100);
      return sum + itemDiscount;
    }, 0);

    // Add order-level discount
    const subtotal = calculateSubtotal();
    const orderDiscount = subtotal * (discountPercentage / 100);

    return itemDiscounts + orderDiscount;
  };

  const calculateTotalTax = () => {
    return cart.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const itemDiscount =
        itemSubtotal * ((item.discountPercentage || 0) / 100);
      const itemAfterDiscount = itemSubtotal - itemDiscount;
      const itemTax = itemAfterDiscount * ((item.taxPercentage || 0) / 100);
      return sum + itemTax;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const totalDiscount = calculateTotalDiscount();
    const totalTax = calculateTotalTax();
    return Math.max(0, subtotal - totalDiscount + totalTax);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      message.warning("Cart is empty. Please add items to cart.");
      return;
    }
    if (!selectedPatient?.id) {
      message.warning("Please select a patient before checkout.");
      return;
    }
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSave = async (paymentData: PaymentData) => {
    if (!selectedPatient?.id) {
      message.warning("Please select a patient");
      return;
    }

    if (cart.length === 0) {
      message.warning("Cart is empty. Please add items to cart.");
      return;
    }

    const subtotal = calculateSubtotal();
    const totalDiscount = calculateTotalDiscount();
    const totalTax = calculateTotalTax();
    const netAmount = subtotal - totalDiscount + totalTax;
    const totalPaid =
      (paymentData.upi || 0) +
      (paymentData.cash || 0) +
      (paymentData.card || 0) +
      (paymentData.cheque || 0) +
      (paymentData.online || 0);

    // if (totalPaid < netAmount) {
    //   message.warning(
    //     `Payment amount (₹${totalPaid}) is less than total (₹${netAmount})`
    //   );
    //   return;
    // }

    // Convert cart items to backend format; for batch-tracked items use batchId or resolve nearest-expiry batch
    const saleItems = cart.map((item) => {
      const pharmacyItem = pharmacyItems.find((pi) => pi.id === item.pharmacyItemId);
      let batchIdForPayload: number | null = item.batchId ?? null;
      if (pharmacyItem?.is_batch_tracked && batchIdForPayload == null) {
        const nearestBatch = getBatchWithNearestExpiry(pharmacyItem);
        batchIdForPayload = nearestBatch?.id ?? null;
      }
      if (pharmacyItem?.is_batch_tracked && batchIdForPayload == null) {
        throw new Error(`Batch is required for batch-tracked item: ${item.name}. No usable batch found.`);
      }
      return {
        pharmacy_item_id: item.pharmacyItemId,
        pharmacy_batch_id: batchIdForPayload,
        quantity: item.quantity,
        mrp: item.mrp || item.unitPrice,
        selling_price: item.unitPrice,
        discount_percentage: item.discountPercentage || 0,
        tax_percentage: item.taxPercentage || 0,
      };
    });

    // Prepare sale data matching backend API structure
    // Backend expects: patient_id, visit_id, doctor_id, sale_date, discount_percentage (order-level only), status, notes, items
    const saleData = {
      patient_id: selectedPatient?.id || null,
      visit_id: null, // Can be added later if needed
      doctor_id: selectedUserId || null,
      sale_date: saleDate.toISOString().split("T")[0],
      discount_percentage: discountPercentage || 0, // Order-level discount percentage (0-100)
      status: totalPaid >= netAmount ? "paid" : "partially_paid",
      notes: notes || paymentData.paymentNote || null,
      items: saleItems,
    };

    try {
      // Create the pharmacy sale
      // saleData is in backend format (snake_case) matching StorePharmacySaleRequest validation
      const response: any = await createPOSSale(saleData);
      const saleId = response?.data?.id || response?.id;

      if (!saleId) {
        throw new Error("Sale ID not returned from API");
      }

      // Create payment records using new format
      const { processPayment } = await import("../../apis/payment");
      
      // Build payments array with all payment modes
      const payments: any[] = [];
      const baseTimestamp = Date.now();
      
      if (paymentData.cash > 0) {
        payments.push({
          amount: paymentData.cash,
          payment_mode: "cash",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CASH`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.upi > 0) {
        payments.push({
          amount: paymentData.upi,
          payment_mode: "upi",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-UPI`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.card > 0) {
        payments.push({
          amount: paymentData.card,
          payment_mode: "card",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CARD`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.cheque > 0) {
        payments.push({
          amount: paymentData.cheque,
          payment_mode: "cheque",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-CHEQUE`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      if (paymentData.online > 0) {
        payments.push({
          amount: paymentData.online,
          payment_mode: "online",
          payment_date: saleDate.toISOString().split("T")[0],
          transaction_id: `TXN-${baseTimestamp}-ONLINE`,
          notes: paymentData.paymentNote || null,
          allocation_type: "manual",
          allocation_notes: paymentData.paymentNote || null,
        });
      }

      // Process all payments in a single API call using new format
      if (payments.length > 0) {
        await processPayment({
          source_type: "pharmacy_sale",
          source_ids: [saleId],
          payments: payments,
          source_amounts: {
            [saleId]: netAmount,
          },
          invoice_date: saleDate.toISOString().split("T")[0],
        });
      }

      showNotification(
        "success",
        "Sale and payment completed successfully!",
        "",
        "topRight"
      );

      // Reset cart and form
      setCart([]);
      setSelectedPatient(null);
      setSelectedUserId(null);
      setDiscount(0);
      setDiscountPercentage(0);
      setNotes("");
      setSaleDate(new Date());
      setIsPaymentModalOpen(false);

      // Print receipt (optional)
      // if (response?.data || response) {
      //   printReceipt(response?.data || response);
      // }
    } catch (error: any) {
      console.error("Error creating sale:", error);
      showNotification(
        "error",
        error?.response?.data?.error?.details ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to complete sale",
        "",
        "topRight"
      );
    }
  };

  const handlePrintReceipt = () => {
    // This will be called from PaymentModal's print button
    // For now, we'll handle it after save
  };

  const printReceipt = (saleData: any) => {
    // Simple receipt printing (can be enhanced with a proper receipt component)
    const receiptWindow = window.open("", "_blank");
    if (receiptWindow) {
      const totalPaid =
        saleData.payments?.reduce(
          (sum: number, payment: any) => sum + (payment.amount || 0),
          0
        ) || 0;
      const total = saleData.total || calculateTotal();
      const receiptContent = `
        <html>
          <head>
            <title>Receipt</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .header { text-align: center; margin-bottom: 20px; }
              .item { margin: 10px 0; }
              .total { font-weight: bold; margin-top: 20px; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>Pharmacy Receipt</h2>
              <p>Sale #${saleData.saleNumber || saleData.id}</p>
              <p>Date: ${format(
                new Date(saleData.saleDate || new Date()),
                "dd MMM yyyy, HH:mm"
              )}</p>
            </div>
            ${
              selectedPatient
                ? `<p><strong>Patient:</strong> ${selectedPatient.firstName} ${selectedPatient.lastName}</p>`
                : ""
            }
            <hr>
            ${(saleData.items || cart)
              .map(
                (item: any) => `
              <div class="item">
                <p><strong>${item.name}</strong> - ${item.quantity} x ₹${item.unitPrice} = ₹${item.total}</p>
              </div>
            `
              )
              .join("")}
            <hr>
            <div class="total">
              <p>Subtotal: ₹${saleData.subtotal || calculateSubtotal()}</p>
              ${
                (saleData.discount || discount) > 0
                  ? `<p>Discount: -₹${saleData.discount || discount}</p>`
                  : ""
              }
              <p>Total: ₹${total}</p>
              <p>Paid: ₹${totalPaid}</p>
              ${totalPaid > total ? `<p>Change: ₹${totalPaid - total}</p>` : ""}
            </div>
            <div class="footer">
              <p>Thank you for your purchase!</p>
            </div>
          </body>
        </html>
      `;
      receiptWindow.document.write(receiptContent);
      receiptWindow.document.close();
      receiptWindow.print();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && filteredItems.length > 0) {
      addToCart(filteredItems[0]);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-2 shadow-sm flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">Pharmacy POS</h1>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-2">
        {/* Row 1: Sale date, Select patient, Select user — one line */}
        <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm p-3 mb-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sale Date
              </label>
              <DatePicker
                selected={saleDate}
                onChange={(date: Date | null) =>
                  setSaleDate(date || new Date())
                }
                dateFormat="dd MMM yyyy"
                className="w-full h-8 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Select Patient
              </label>
              <Select
                showSearch
                size="small"
                style={{ width: "100%", height: "30px" }}
                placeholder="Search and select patient..."
                value={selectedPatient?.id || undefined}
                onChange={(value) => {
                  const patient = patients.find((p) => p.id === value);
                  setSelectedPatient(patient || null);
                }}
                filterOption={(input, option: any) => {
                  const patient = patients.find((p) => p.id === option?.value);
                  if (!patient) return false;
                  const searchText = input.toLowerCase();
                  const fullName = `${patient.firstName || ""} ${
                    patient.lastName || ""
                  }`.toLowerCase();
                  return (
                    fullName.includes(searchText) ||
                    (!!patient.phone &&
                      patient.phone.toLowerCase().includes(searchText)) ||
                    (!!patient.email &&
                      patient.email.toLowerCase().includes(searchText))
                  );
                }}
                allowClear
              >
                {patients.map((patient) => (
                  <Option key={patient.id} value={patient.id}>
                    {patient.firstName} {patient.lastName}{" "}
                    {patient.phone && `- ${patient.phone}`}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Select User
              </label>
              <Select
                showSearch
                size="small"
                style={{ width: "100%", height: "30px" }}
                placeholder="Search and select user..."
                value={selectedUserId || undefined}
                onChange={(value) => setSelectedUserId(value)}
                filterOption={(input, option: any) => {
                  const user = users.find((u) => u.id === option?.value);
                  if (!user) return false;
                  const searchText = input.toLowerCase();
                  const fullName = `${user.firstName || ""} ${
                    user.lastName || ""
                  }`.toLowerCase();
                  return (
                    fullName.includes(searchText) ||
                    (!!user.phone &&
                      user.phone.toLowerCase().includes(searchText)) ||
                    (!!user.email &&
                      user.email.toLowerCase().includes(searchText)) ||
                    (!!user.registrationNumber &&
                      user.registrationNumber
                        .toLowerCase()
                        .includes(searchText))
                  );
                }}
                allowClear
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}{" "}
                    {user.phone && `- ${user.phone}`}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Row 2: Cart section — two columns: cart list (large) | summary + notes */}
        <div className="flex-1 flex gap-4 min-h-0">
          {/* Left: Add items strip + Cart list (large scrollable area) */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {/* Compact add-items row — single line */}
            <div className="flex-shrink-0 py-2 px-2 border-b border-gray-200 bg-gray-50/80">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Add Bundle
                  </label>
                  <Select
                    size="small"
                    style={{ width: "100%" }}
                    placeholder="Select bundle..."
                    value={selectedBundleId ?? undefined}
                    onChange={(value) => setSelectedBundleId(value ?? null)}
                    allowClear
                    onClear={() => {
                      setSelectedBundleId(null);
                      setBundleDetail(null);
                    }}
                  >
                    {bundles
                      .filter((b) => b.isActive ?? b.is_active ?? true)
                      .map((b) => (
                        <Option key={b.id} value={b.id}>
                          {b.name} ({b.code})
                        </Option>
                      ))}
                  </Select>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Add Item
                  </label>
                  <Select
                    showSearch
                    size="small"
                    style={{ width: "100%" }}
                    placeholder="Search pharmacy item..."
                    value={undefined}
                    onChange={(value) => {
                      const selectedItem = pharmacyItems.find(
                        (pi) => pi.id === value
                      );
                      if (selectedItem) handleItemSelect(selectedItem);
                    }}
                    filterOption={(input, option: any) => {
                      const selectedItem = pharmacyItems.find(
                        (pi) => pi.id === option?.value
                      );
                      if (!selectedItem) return false;
                      const searchText = input.toLowerCase();
                      return (
                        selectedItem.name.toLowerCase().includes(searchText) ||
                        selectedItem.sku.toLowerCase().includes(searchText) ||
                        (!!selectedItem.barcode &&
                          selectedItem.barcode.toLowerCase().includes(searchText)) ||
                        (!!selectedItem.molecule &&
                          selectedItem.molecule.toLowerCase().includes(searchText))
                      );
                    }}
                  >
                    {pharmacyItems.map((pharmacyItem) => (
                      <Option
                        key={pharmacyItem.id}
                        value={pharmacyItem.id}
                        disabled={!hasUsableBatches(pharmacyItem)}
                      >
                        <span className="flex items-center gap-2 flex-wrap text-sm">
                          <span>
                            {pharmacyItem.name} - {pharmacyItem.sku}
                            {pharmacyItem.form && ` (${pharmacyItem.form})`}
                          </span>
                          {!hasUsableBatches(pharmacyItem) && (
                            <Tag color="red">Not available</Tag>
                          )}
                        </span>
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
              {bundleDetailLoading && selectedBundleId && (
                <span className="text-xs text-gray-500 mt-1 block">Adding bundle to cart...</span>
              )}
            </div>

            {/* Cart list: large scrollable area — min-height ensures comfortable space */}
            <div className="flex-1 min-h-[380px] overflow-y-auto flex flex-col">
              <div className="flex-shrink-0 px-2 pt-2 pb-1 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <ShoppingCartOutlined />
                  Cart ({cart.length})
                </h2>
              </div>
              <div className="flex-1 p-2 overflow-y-auto min-h-0">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-gray-500 py-8 px-2">
                <ShoppingCartOutlined className="text-3xl text-gray-300 mb-1" />
                <p className="text-sm">Cart is empty.</p>
                <p className="text-xs mt-0.5">Use the fields above to add a bundle or item.</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {cart.map((item:any) => {
                  const itemData = pharmacyItems.find(
                    (pi) => pi.id === item.pharmacyItemId
                  );
                  return (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded py-1.5 px-2 shadow-sm flex items-center gap-2 flex-nowrap"
                    >
                      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                        <div className="flex items-center gap-1.5 flex-nowrap overflow-hidden">
                          <span className="text-sm text-gray-900 font-medium truncate">{item.name}</span>
                          {item.batchNumber && (
                            <Tag
                              color="blue"
                              className="cursor-pointer flex-shrink-0 text-xs"
                              onClick={() => {
                                if (itemData) {
                                  setSelectedItemForBatch(itemData);
                                  setSelectedCartItemForBatch(item.id);
                                  fetchBatchesForItem(itemData);
                                  setIsBatchModalOpen(true);
                                }
                              }}
                            >
                              {item.batchNumber}
                            </Tag>
                          )}
                        </div>
                        <div className="text-gray-500 text-xs flex-shrink-0 mt-0.5">
                          {item.sku && <span>• {item.sku}</span>}
                          {item.form && <span> • {item.form}</span>}
                          {item.strength && <span> • {item.strength}</span>}
                          {(item.mrp ?? 0) > 0 && (
                            <span> • MRP ₹{Number(item.mrp).toFixed(2)}</span>
                          )}
                          {(item.taxPercentage ?? 0) > 0 && (
                            <span> • Tax {item.taxPercentage}%</span>
                          )}
                           
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="small"
                          type="text"
                          className="!p-0.5 !min-w-0"
                          icon={<MinusOutlined />}
                          onClick={() => updateCartItemQuantity(item.id, -1)}
                        />
                        <Input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => {
                            const qty = parseInt(e.target.value) || 1;
                            setCart(
                              cart.map((cartItem) => {
                                if (cartItem.id === item.id) {
                                  const updatedItem = {
                                    ...cartItem,
                                    quantity: qty,
                                  };
                                  return {
                                    ...updatedItem,
                                    total: calculateItemTotal(updatedItem),
                                  };
                                }
                                return cartItem;
                              })
                            );
                          }}
                          className="!w-12 !h-7 text-center text-xs rounded"
                        />
                        <Button
                          size="small"
                          type="text"
                          className="!p-0.5 !min-w-0"
                          icon={<PlusOutlined />}
                          onClick={() => updateCartItemQuantity(item.id, 1)}
                        />
                      </div>
                      {/* Taxable amount (reverse-calculated: mrp / (1 + gst%)) shown ahead of + sign; editable tax % and discount % */}
                      <div className="flex items-center gap-0.5 flex-shrink-0 flex-wrap">
                        <Input
                          type="number"
                          prefix="₹"
                          min={0}
                          step={0.01}
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateCartItemPrice(
                              item.id,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-20 !h-7 text-xs"
                          placeholder="0"
                         readOnly
                          title="Taxable amount (MRP ÷ (1 + tax%))"
                        />
                        {/* <span className="text-xs text-gray-500">Disc</span>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={item.discountPercentage ?? 0}
                          onChange={(e) =>
                            updateCartItemDiscountPercentage(
                              item.id,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-16 !h-7 text-xs text-center"
                          suffix="%"
                          title="Discount %"
                        /> */}
                      </div>
                      <div className="text-right w-16 flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-900">₹{(item.total).toFixed(2)}</span>
                      </div>
                      <Button
                        type="text"
                        danger
                        size="small"
                        className="!p-0.5 !min-w-0 flex-shrink-0"
                        icon={<DeleteOutlined />}
                        onClick={() => removeFromCart(item.id)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
              </div>
            </div>
          </div>

          {/* Right column: Summary + Notes — fixed width, always visible */}
          <div className="w-full sm:w-[360px] flex-shrink-0 flex flex-col gap-2 min-h-0 overflow-y-auto">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <Input.TextArea
                size="middle"
                placeholder="Add notes for this sale..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <ShoppingCartOutlined />
                Order Summary
              </h3>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-gray-700 text-sm">
                  <span>Subtotal</span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700 text-sm">
                  <span>Tax</span>
                  <span>₹{calculateTotalTax().toFixed(2)}</span>
                </div>
                {/* <div className="flex justify-between items-center gap-2 text-sm">
                  <span className="text-gray-700">Discount %</span>
                  <Input
                    type="number"
                    suffix="%"
                    value={discountPercentage}
                    onChange={(e) => {
                      const percentage = parseFloat(e.target.value) || 0;
                      setDiscountPercentage(
                        Math.min(100, Math.max(0, percentage))
                      );
                      const subtotal = calculateSubtotal();
                      setDiscount(subtotal * (percentage / 100));
                    }}
                    className="w-20"
                    placeholder="0"
                    min={0}
                    max={100}
                  />
                </div>
                <div className="flex justify-between text-gray-700 text-sm">
                  <span>Discount</span>
                  <span>₹{calculateTotalDiscount().toFixed(2)}</span>
                </div> */}
                <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span>₹{calculateTotal().toFixed(2)}</span>
                </div>
              </div>
              <Button
                type="primary"
                size="large"
                block
                onClick={handleCheckout}
                disabled={cart.length === 0}
                className="bg-primary hover:bg-hover"
              >
                Checkout
              </Button>
            </div>
            
          </div>
        </div>
      </div>

      {/* Batch Selection Modal */}
      {isBatchModalOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              aria-hidden="true"
              onClick={() => {
                setIsBatchModalOpen(false);
                setSelectedItemForBatch(null);
                setSelectedCartItemForBatch(null);
                setSelectedBatch(null);
                setAvailableBatches([]);
                setQuantityToAdd(1);
              }}
            ></div>

            {/* Modal */}
            <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Select Batch - {selectedItemForBatch?.name}
                  {quantityToAdd > 1 && (
                    <span className="text-base font-normal text-gray-600 ml-2">
                      (Qty: {quantityToAdd})
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setSelectedItemForBatch(null);
                    setSelectedCartItemForBatch(null);
                    setSelectedBatch(null);
                    setAvailableBatches([]);
                    setQuantityToAdd(1);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <CloseOutlined className="text-xl" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4">
                {isLoadingBatches ? (
                  <div className="text-center py-8">Loading batches...</div>
                ) : availableBatches.length === 0 ? (
                  <div className="space-y-4">
                    <div className="text-center py-4 text-gray-500">
                      No batches available for this item. You can still add it
                      without a batch.
                    </div>
                    <Button
                      type="primary"
                      block
                      onClick={() => {
                        setSelectedBatch(null);
                        addToCartWithBatch();
                      }}
                      className="bg-primary hover:bg-hover"
                    >
                      Add Without Batch
                    </Button>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <div className="space-y-3">
                      {availableBatches.map((batch: PharmacyBatch) => {
                        const expiryDate = batch.expiry_date;
                        const mfgDate = batch.manufacturing_date;
                        const availableQty = batch.available_quantity || 0;
                        const sellingPrice = parseFloat(
                          batch.selling_price || "0"
                        );
                        const mrp = batch.mrp || sellingPrice;
                        const isExpired =
                          expiryDate && new Date(expiryDate) < new Date();
                        const isLowStock = availableQty <= 10;

                        return (
                          <div
                            key={batch.id}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              selectedBatch?.id === batch.id
                                ? "border-primary bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            } ${isExpired ? "bg-red-50 border-red-300" : ""}`}
                            onClick={() => setSelectedBatch(batch)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-gray-900">
                                    Batch: {batch.batch_number}
                                  </h4>
                                  {isExpired && <Tag color="red">Expired</Tag>}
                                  {isLowStock && !isExpired && (
                                    <Tag color="orange">Low Stock</Tag>
                                  )}
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                                  <div>
                                    <span className="text-gray-600">
                                      Available Qty:
                                    </span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      {availableQty}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">MRP:</span>
                                    <span className="ml-2 font-medium text-gray-900">
                                      ₹{mrp.toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">
                                      Selling Price:
                                    </span>
                                    <span className="ml-2 font-medium text-primary">
                                      ₹{sellingPrice.toFixed(2)}
                                    </span>
                                  </div>
                                  {expiryDate && (
                                    <div>
                                      <span className="text-gray-600">
                                        Expiry Date:
                                      </span>
                                      <span
                                        className={`ml-2 font-medium ${
                                          isExpired
                                            ? "text-red-600"
                                            : "text-gray-900"
                                        }`}
                                      >
                                        {format(
                                          new Date(expiryDate),
                                          "dd MMM yyyy"
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {mfgDate && (
                                    <div>
                                      <span className="text-gray-600">
                                        Mfg Date:
                                      </span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        {format(
                                          new Date(mfgDate),
                                          "dd MMM yyyy"
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {batch.purchase_price && (
                                    <div>
                                      <span className="text-gray-600">
                                        Purchase Price:
                                      </span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        ₹
                                        {parseFloat(
                                          batch.purchase_price || "0"
                                        ).toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="ml-4">
                                {selectedBatch?.id === batch.id && (
                                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                    <span className="text-white text-xs">
                                      ✓
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <Button
                  onClick={() => {
                    setIsBatchModalOpen(false);
                    setSelectedItemForBatch(null);
                    setSelectedCartItemForBatch(null);
                    setSelectedBatch(null);
                    setAvailableBatches([]);
                    setQuantityToAdd(1);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={addToCartWithBatch}
                  disabled={!selectedBatch && availableBatches.length > 0}
                  className="px-6 py-2 text-white bg-primary hover:bg-hover rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {selectedCartItemForBatch ? "Update Batch" : "Add to Cart"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        total={calculateTotal()}
        discount={calculateTotalDiscount()}
        sourceType="pharmacy"
        sourceId={selectedUserId ?? 0}
        patientId={selectedPatient?.id}
        patientName={
          selectedPatient
            ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
            : "Counter Sale"
        }
        onSave={handlePaymentSave}
        // onPrint={handlePrintReceipt}
      />
    </div>
  );
};

export default PharmacyPOS;
